//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import MessageIcon from '@material-ui/icons/Message';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import BusinessIcon from '@material-ui/icons/Business';
import SchoolIcon from '@material-ui/icons/School';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { makeStyles, Badge, Menu, MenuItem, Tooltip, Link, IconButton } from '@material-ui/core';
import PageviewOutlinedIcon from '@material-ui/icons/PageviewOutlined';
import VerifiedUserRoundedIcon from '@material-ui/icons/VerifiedUserRounded';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { mdiSchoolOutline } from '@mdi/js';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { DASHBOARD_MY_MESSAGES, COMPANY_NAME_MISSING, CONFIRM_DIALOG_TITLE_DELETE_JOB, CONFIRM_DIALOG_MESSAGE_DELETE_JOB, VISIT_BRAND_PAGE } from '../../common/constants';
import { getCityStateCountryText, getJobYearsOfExperienceText, getSalaryRateText } from '../../common/textHelper';
import { getUniqueKey } from '../../common/arrayHelper';
import { getFormattedDate, getUserDateFormat } from '../../common/dateHelper';
import { bookmarkJob } from '../../common/services/jobCenterServices';
import { getBrandPageUrlPathByEmployerId, getEmpMyJobShareUrl } from '../../common/linkHelper';
import { copyTextToClipboard } from '../../system/helpers/systemHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveMyJobsNote } from '../../common/services/myJobsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdMdiIcon from '../../common/elemental/AfitdMdiIcon';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdIconButton from '../../common/elemental/AfitdIconButton';
import AfitdNote from '../../common/elemental/AfitdNote';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _split from 'lodash/split';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { active } = params;

    return makeStyles(theme => ({
        root: { display: 'flex', alignItems: 'stretch', borderTop: '1px solid rgb(240,242,246)', padding: '24px 0' },
        leftContainer: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' },
        rightContainer: { flex: 1 },

        logoLinkContainer: {
            cursor: 'pointer',
        },
        companyProfilePic: {
            width: 100,
            height: 100,
            marginRight: '24px',
            border: '1px solid #D5D7DD',
            background: 'rgba(252, 130, 31, 0.1)',
            color: 'rgb(252,130,31)',
            fontSize: '35px',
        },
        companyNameLink: {
            textDecoration: 'underline',
            color: active ? undefined : '#9B9E9C',
        },
        jobLocationText: { fontWeight: 'normal', marginBottom: 10 },
        addNoteButton: { marginTop: 24, marginBottom: 28 },
        addNoteButtonLabel: { textTransform: 'none' },
        // addNoteIcon: { color: '#9B9E9C' },

        headingSection: {
            display: 'flex',
            flex: 1,
            justifyContent: 'space-between',
            [theme.breakpoints.down('lg')]: { justifyContent: 'flex-start', flexDirection: 'column-reverse' },
        },
        heading: {
            fontWeight: 'bold',
            marginRight: '10px',
            [theme.breakpoints.down('xl')]: {
                maxWidth: '460px',
            },
            [theme.breakpoints.down('lg')]: {
                maxWidth: '430px',
            },
            [theme.breakpoints.down('md')]: {
                maxWidth: '420px',
            },
            [theme.breakpoints.down('sm')]: {
                maxWidth: '300px',
                marginRight: '0px',
            },
        },
        viewJobDetailIconToolTip: { marginLeft: 20, '&.MuiIconButton-root:hover': { background: 'rgba(0, 0, 0, 0)' } },
        viewSingleJobIconButton: { width: 12, height: 12 },
        deleteIconButton: { width: 12, height: 12 },
        titleContainer: { cursor: 'pointer', display: 'flex', alignItems: 'center', maxWidth: '60%' },
        statusText: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', marginRight: 15 },
        statusDateLabel: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', fontStyle: 'italic', marginRight: 8 },
        statusDateText: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', marginRight: 15 },
        verifyBadgeIcon: active ? { color: '#1D8A8B', margin: '0 5px' } : { color: '#1D8A8B', opacity: 0.6 },
        companyContainer: { padding: '10px 0', display: 'flex' },
        detailContainer: { flexDirection: 'row', display: 'flex', alignItems: 'center', marginRight: 20 },
        detail: { fontWeight: 'bold' },
        icons: { color: '#9B9E9C', fontSize: 20, marginRight: 10 },
        messageIcon: { color: '#9B9E9C', fontSize: 20 },
        statusInfoContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', [theme.breakpoints.down('lg')]: { justifyContent: 'space-between' } },
        miscInfoContainer: { display: 'flex', justifyContent: 'space-between' },
        miscInfo: { display: 'flex', flex: 1, flexWrap: 'wrap' },
        comingSoonContainer: { margin: '15px 125px 0 0', wordBreak: 'break-word' },
        filledContainer: { margin: '15px 125px 0 0', wordBreak: 'break-word' },
        descriptionTextContainer: { margin: '15px 125px 0 0', wordBreak: 'break-word' },
        // savedListText: { color: 'rgb(29,138,139)', fontWeight: 'bold', marginRight: '10px' },
        // appliedListText: { color: 'rgb(252,130,31)', fontWeight: 'bold', marginRight: '10px' },
        // settingsIcon: { cursor: 'pointer' },
        // savedList: { backgroundColor: 'rgba(29,138,139,0.09)' },
        // appliedList: { backgroundColor: '#FFF8E5' },
        // list: {
        //     borderRadius: '4px',
        //     marginTop: '16px',
        //     padding: '15px',
        //     display: 'flex',
        //     flexDirection: 'row',
        //     alignItems: 'center',
        //     justifyContent: 'space-between',
        //     cursor: 'pointer',
        // },
        badge: { cursor: 'pointer' },
        inactive: { color: '#9B9E9C' },
        inactiveStatus: { color: 'red' },
        inactiveJobDate: { color: '#111E14' },
        // inactiveVerfiedBadge: { color: '#1D8A8B', opacity: 0.6 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        classes,
        userDateFormat,
        // // handleShowListDialog,
        // listModalOpen,
        // isSavedList,
        // setListModalOpen,
        setAnchorEl,
        anchorEl,
        active,
        handleDialog,
        handleClickDeleteButton,
        confirmDeleteDialogOpen,
        setConfirmDeleteDialogOpen,
        handleClickConfirmDeleteDialogConfirm,
        // limitModal,
        // setLimitModal,
        // appliedJobSeekers,
        // editModalShow,
        // setEditModalShow,
        // hiredModalOpen,
        // setHiredModalOpen,
        // savedJobSeekers,
        jobId,
        unreadNewApplicantsAll,
        handleClickAddNoteButton,
        isNoteEnabled,
        setIsNoteEnabled,
        handleSaveNote,
        handleCancelNote,
        statusDt,
        applyDt,
        updateDt,
        handleClickCompanyLogo,
        mdMatchesUp,
        handleClickShareJobUrlMenu,
    } = params;
    const { isSavedJob, job, idx, myJobsData, onJobTitleClick } = props;
    const { inactive: inactiveCls, inactiveStatus: inactiveStatusCls } = classes;

    // If Applied, shows 'Applied {status date}'
    // If Saved and inactive, shows 'Since {status date}'
    // If Saved and active and updated afterward, shows 'Updated on {last update date}'
    // If Saved and active and never updated, shows 'Posted {status date}'

    const isUpdatedAfterActive = updateDt > statusDt;
    const dateInfoLabel = !isSavedJob ? 'Applied' : !active ? 'Since' : isUpdatedAfterActive ? 'Updated on' : 'Posted';
    const dateInfoDate = !isSavedJob ? applyDt : !active ? statusDt : isUpdatedAfterActive ? updateDt : statusDt;

    const unreadNewApplicants = _get(unreadNewApplicantsAll, jobId, []);
    const unreadNewApplicantsLength = unreadNewApplicants.length;
    const isHideUnreadNewApplicantsBadge = unreadNewApplicantsLength === 0;

    const jobEmployerId = _get(job, 'employerId');
    const companyName = _get(myJobsData, `employers.${jobEmployerId}.companyName`, COMPANY_NAME_MISSING);
    const city = _get(job, 'city');
    const state = _get(job, 'state');
    const country = _get(job, 'country');
    const companyProfilePicUrl = _get(myJobsData, `employers.${jobEmployerId}.companyProfilePicUrl`);
    const jobLocationText = getCityStateCountryText(city, state, country);
    const note = _get(job, 'note', '');

    const jobLocationTextColor = active ? null : { clsMidGrayColor: true };

    const defns = {
        // logoContainer: {
        //     onClick: handleClickCompanyLogo,
        //     // () => {
        //     //     alert('Brand Page');
        //     // },
        // },
        employerLogoLink: {
            isBrandPage: false, // My Jobs will never be inside a Brand Page so the logo should always be a link
            onClick: handleClickCompanyLogo,
            profilePicUrl: companyProfilePicUrl,
            brandEmployerId: jobEmployerId,
        },
        // companyProfilePic: {
        //     variant: 'rounded',
        //     src: companyProfilePicUrl,
        // },
        jobTitleBadge: {
            variant: 'dot',
            color: 'primary',
            invisible: isHideUnreadNewApplicantsBadge,
        },
        titleContainer: { onClick: () => onJobTitleClick(idx) },
        viewJobDetailIconToolTip: { title: 'View Job Details' },
        heading: { variant: 'h6', classNameMixin: cx({ [inactiveCls]: !active }) },
        // viewSingleJobIconButton: { classNameMixin: cx({ [inactiveCls]: !active }) },
        statusText: { variant: 'caption', classNameMixin: cx({ [inactiveStatusCls]: !active }), value: active ? 'Active' : 'Inactive' },
        dateInfo: { label: dateInfoLabel, date: dateInfoDate, dateFormat: userDateFormat, active },
        icons: { classNameMixin: cx({ [inactiveCls]: !active }) },
        detail: { variant: 'body1', classNameMixin: cx({ [inactiveCls]: !active }) },
        internshipIcon: {
            // color: 'watermark',
            style: { color: '#9B9E9C', width: 18, marginRight: 10 },
            path: mdiSchoolOutline,
        },
        badge: { color: 'secondary', overlap: 'circle', variant: 'dot' },
        comingSoonText: { variant: 'body1', color: 'secondary', clsSkinnyBold: true },
        filledText: { variant: 'body1', color: 'secondary', clsSkinnyBold: true },
        descriptionText: { variant: 'body2', classNameMixin: cx({ [inactiveCls]: !active }) },
        listText: { variant: 'body2' },
        // savedListText: { variant: 'body2' },
        // savedList: { onClick: () => handleShowListDialog(true), className: cx(listCls, savedListCls) },

        companyNameLink: {
            color: 'secondary',
            title: "Visit Company's Brand Page",
            onClick: mdMatchesUp ? undefined : handleClickCompanyLogo,
            href: mdMatchesUp ? getBrandPageUrlPathByEmployerId(jobEmployerId) : undefined,
            target: mdMatchesUp ? '_blank' : undefined,
        },
        companyNameText: {
            variant: 'body1',
            color: 'secondary',
            value: companyName,
            classNameMixin: cx({ [inactiveCls]: !active }),
        },
        verifyBadgeIcon: {},

        jobLocationText: { variant: 'body1', value: jobLocationText, ...jobLocationTextColor },

        applicantsBadge: {
            color: 'primary',
            badgeContent: unreadNewApplicantsLength,
            invisible: isHideUnreadNewApplicantsBadge,
        },
        // appliedListText: { variant: 'body2' },
        // appliedList: { onClick: () => handleShowListDialog(false), className: cx(listCls, appliedListCls) },
        // listModal: {
        //     open: listModalOpen,
        //     onClose: () => setListModalOpen(false),
        //     title: `Job seekers ${isSavedList ? 'saved' : 'that have applied'} to this job`,
        //     isSavedList,
        //     listType: isSavedList ? EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_SAVED : EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_APPLIED,
        //     limitExceeded,
        //     jobSeekers: isSavedList ? savedJobSeekers : appliedJobSeekers,
        //     job,
        //     unreadNewApplicants,
        // },
        menu: { id: 'long-menu', anchorEl: anchorEl, keepMounted: true, open: Boolean(anchorEl), onClose: () => setAnchorEl(null) },
        moreButton: { 'aria-controls': 'long-menu', 'aria-haspopup': true, onClick: evt => setAnchorEl(evt.currentTarget) },
        deleteOptionMenu: { onClick: () => handleDialog('delete') },
        shareJobUrlMenu: { onClick: handleClickShareJobUrlMenu },
        confirmDeleteDialog: {
            open: confirmDeleteDialogOpen,
            onClose: () => setConfirmDeleteDialogOpen(false),
            onConfirm: () => handleClickConfirmDeleteDialogConfirm(),
            title: CONFIRM_DIALOG_TITLE_DELETE_JOB,
            message: CONFIRM_DIALOG_MESSAGE_DELETE_JOB,
        },

        titleIcon: { title: 'View Job Details' },

        addNoteButton: { onClick: handleClickAddNoteButton },
        note: {
            isHidden: !isNoteEnabled && !note,
            onSave: handleSaveNote,
            onCancel: handleCancelNote,
            isEnabled: isNoteEnabled,
            setIsEnabled: setIsNoteEnabled,
            value: note,
        },

        deleteIconButton: {
            onClick: handleClickDeleteButton,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const DateInfo = props => {
    const classes = useStyles()();
    const { label, date, dateFormat } = props;
    const defns = {
        statusDateLabel: { variant: 'caption', component: 'span', value: `${label}:` },
        statusDateText: { variant: 'caption', component: 'span', value: getFormattedDate(date, dateFormat) },
    };
    setClassNameInDefns(defns, classes);
    return (
        <Fragment>
            <AfitdTypography {...defns.statusDateLabel} />
            <AfitdTypography {...defns.statusDateText} />
        </Fragment>
    );
};

const EmployerLogoLink = props => {
    const { isBrandPage, brandEmployerId, onClick, profilePicUrl } = props;
    const classes = useStyles({ isBrandPage })();
    const theme = useTheme();
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));

    const defns = {
        logoLinkContainer: {
            onClick: isBrandPage ? undefined : onClick,
        },
        logoLink: {
            href: getBrandPageUrlPathByEmployerId(brandEmployerId),
            target: '_blank',
        },
        companyProfilePic: {
            variant: 'rounded',
            src: profilePicUrl,
        },
        tooltip: {
            title: VISIT_BRAND_PAGE,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return isBrandPage ? (
        <div {...defns.logoContainer}>
            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
        </div>
    ) : (
        <div {...defns.logoContainer}>
            <Tooltip {...defns.tooltip}>
                <div>
                    {mdMatchesUp ? (
                        <Link {...defns.logoLink}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </Link>
                    ) : (
                        <div {...defns.logoLinkContainer}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </div>
                    )}
                </div>
            </Tooltip>
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobCard = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const layoutContext = useContext(LayoutContext);
    const { userId, currentEmployer } = appContext;
    const tiers = _get(appContext, 'sys.tiers');
    const { setBrandPageDialogInfo, setSnackbarMessage, setSnackbarErrorMessage, dashboardData, setAsyncCallCount } = layoutContext;
    const history = useHistory();
    const unreadNewApplicantsAll = _get(dashboardData, 'unreadNewApplicants');
    const userDateFormat = getUserDateFormat(appContext);

    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [savedJobSeekers, setSavedJobSeekers] = useState([]);
    const [appliedJobSeekers, setAppliedJobSeekers] = useState([]);
    const [limitModal, setLimitModal] = useState(false);
    const [listModalOpen, setListModalOpen] = useState(false);
    const [isSavedList, setIsSavedList] = useState(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [editModalShow, setEditModalShow] = useState(false);
    const [hiredModalOpen, setHiredModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isNoteEnabled, setIsNoteEnabled] = useState(false);

    //---------------------------------------------------------------------
    // from props
    //---------------------------------------------------------------------
    const { job, myJobsData, isSavedJob, onJobDelete, onJobNoteSave } = props;
    const jobId = _get(job, 'uid');
    const jobEmployerId = _get(job, 'employerId');
    const isEmpBadge = _get(myJobsData, `employers.${jobEmployerId}.isEmpBadge`);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const {
        statusDt,
        applyDt,
        updateDt,
        jobDescription,
        education,
        isExactRate,
        rateMin,
        rateMax,
        intern,
        jobTitle,
        // createDt,
        currency,
        currencySymbol,
        employmentType,
        telework,
        active,
        jobSeekers,
        conversations,
        applicants,
        savedJs,
        payFrequency,
        otherEducation,
        yrsOfExp,
        comingSoon,
        filled,
        // rate,
    } = job;
    const conversationExists = (conversations || []).find(el => el.members.includes(userId));
    const yrsOfExpText = getJobYearsOfExperienceText(yrsOfExp);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (job) {
            setAppliedJobSeekers(_map(applicants, jobSeekerId => ({ ..._get(jobSeekers, jobSeekerId), userId: jobSeekerId })));
            setSavedJobSeekers(_map(savedJs, jobSeekerId => ({ ..._get(jobSeekers, jobSeekerId), userId: jobSeekerId })));
        }
    }, [job, applicants, savedJs, jobSeekers]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleShowListDialog = val => {
        setIsSavedList(val);
        setListModalOpen(true);
    };

    const handleDialog = val => {
        // setValue(val);
        setAnchorEl(null);
        setConfirmDeleteDialogOpen(true);
    };

    const handleClickShareJobUrlMenu = async () => {
        setAnchorEl(null);
        const shareUrl = getEmpMyJobShareUrl({ jobId: job?.uid });
        await copyTextToClipboard(shareUrl);
        setSnackbarMessage('Job URL successfully copied to clipboard');
    };

    const handleClickAddNoteButton = () => {
        setIsNoteEnabled(true);
    };

    const handleSaveNote = async note => {
        const success = await saveMyJobsNote({ userId, jobId, note, isSavedJob, setIsNoteEnabled, setSnackbarMessage, setSnackbarErrorMessage });
        success && onJobNoteSave && onJobNoteSave(jobId, note);
    };

    const handleCancelNote = () => {
        setIsNoteEnabled(false);
    };

    const handleClickDeleteButton = () => {
        setConfirmDeleteDialogOpen(true);
    };

    const handleClickConfirmDeleteDialogConfirm = async () => {
        setAsyncCallCount && setAsyncCallCount(prev => prev + 1);
        const success = await bookmarkJob({ isAdd: false, userId, jobId, setSnackbarMessage, setSnackbarErrorMessage });
        success && onJobDelete && onJobDelete(jobId);
        setAsyncCallCount && setAsyncCallCount(prev => prev - 1);
        setConfirmDeleteDialogOpen(false);
    };

    const handleClickCompanyLogo = evt => {
        evt.stopPropagation();
        setBrandPageDialogInfo({ open: true, employerId: jobEmployerId });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ active })();
    const defns = getDefns({
        props,
        classes,
        userDateFormat,
        tiers,
        currentEmployer,
        handleShowListDialog,
        listModalOpen,
        isSavedList,
        setListModalOpen,
        setAnchorEl,
        anchorEl,
        active,
        handleDialog,
        handleClickDeleteButton,
        confirmDeleteDialogOpen,
        setConfirmDeleteDialogOpen,
        handleClickConfirmDeleteDialogConfirm,
        limitModal,
        setLimitModal,
        appliedJobSeekers,
        editModalShow,
        setEditModalShow,
        hiredModalOpen,
        setHiredModalOpen,
        savedJobSeekers,
        jobId,
        unreadNewApplicantsAll,
        handleClickAddNoteButton,
        isNoteEnabled,
        setIsNoteEnabled,
        handleSaveNote,
        handleCancelNote,
        statusDt,
        applyDt,
        updateDt,
        handleClickCompanyLogo,
        mdMatchesUp,
        handleClickShareJobUrlMenu,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            {smMatches ? null : (
                <div {...defns.leftContainer}>
                    <EmployerLogoLink {...defns.employerLogoLink} />
                    <AfitdButton
                        variant="contained"
                        label="Add Note"
                        size="small"
                        labelStyles={classes.addNoteButtonLabel}
                        {...defns.addNoteButton}
                        startIcon={<FormatQuoteIcon {...defns.addNoteIcon} />}
                    />
                </div>
            )}

            <div {...defns.rightContainer}>
                <div {...defns.headingSection}>
                    <div {...defns.titleContainer}>
                        <Badge {...defns.jobTitleBadge}>
                            <AfitdTypography {...defns.heading}>{jobTitle}</AfitdTypography>
                        </Badge>
                        <Tooltip {...defns.viewJobDetailIconToolTip}>
                            <AfitdIconButton {...defns.viewSingleJobIconButton}>
                                <PageviewOutlinedIcon />
                            </AfitdIconButton>
                        </Tooltip>
                    </div>

                    <div {...defns.statusInfoContainer}>
                        <div>
                            <AfitdTypography {...defns.statusText} />
                            <DateInfo {...defns.dateInfo} />
                        </div>
                        {/* {isSavedJob && (
                            <AfitdIconButton {...defns.deleteIconButton}>
                                <CloseIcon />
                            </AfitdIconButton>
                        )} */}
                        <IconButton {...defns.moreButton}>
                            <MoreVertIcon {...defns.settingsIcon} />
                        </IconButton>
                        <Menu {...defns.menu}>
                            <MenuItem {...defns.shareJobUrlMenu}>Share Job URL</MenuItem>
                            {isSavedJob && <MenuItem {...defns.deleteOptionMenu}>Delete Job</MenuItem>}
                        </Menu>
                    </div>
                </div>

                <div {...defns.companyContainer}>
                    <AfitdLink {...defns.companyNameLink}>
                        <AfitdTypography {...defns.companyNameText} />
                    </AfitdLink>
                    {/* <AfitdLink {...defns.companyNameLink} /> */}
                    {/* <AfitdTypography {...defns.companyNameText} /> */}
                    {isEmpBadge && <VerifiedUserRoundedIcon {...defns.verifyBadgeIcon} />}
                </div>
                <AfitdTypography {...defns.jobLocationText} />
                <div {...defns.miscInfoContainer}>
                    <div {...defns.miscInfo}>
                        <div {...defns.detailContainer}>
                            <AfitdTypography {...defns.icons}>{currencySymbol}</AfitdTypography>
                            <AfitdTypography {...defns.detail}>{getSalaryRateText(isExactRate, rateMin, rateMax, currency, payFrequency)}</AfitdTypography>
                        </div>
                        <div {...defns.detailContainer}>
                            <AccessTimeIcon {...defns.icons} />
                            <AfitdTypography {...defns.detail}>{yrsOfExpText}</AfitdTypography>
                        </div>
                        <div {...defns.detailContainer}>
                            <AccessTimeIcon {...defns.icons} />
                            <AfitdTypography {...defns.detail}>{employmentType}</AfitdTypography>
                        </div>
                        <div {...defns.detailContainer}>
                            <BusinessIcon {...defns.icons} />
                            <AfitdTypography {...defns.detail}>{telework}</AfitdTypography>
                        </div>
                        {intern ? (
                            <div {...defns.detailContainer}>
                                <AfitdMdiIcon {...defns.internshipIcon} />
                                <AfitdTypography {...defns.detail}>Intern Position</AfitdTypography>
                            </div>
                        ) : null}
                        <div {...defns.detailContainer}>
                            <SchoolIcon {...defns.icons} />
                            <AfitdTypography {...defns.detail}>{education}</AfitdTypography>
                        </div>
                        {otherEducation?.map(el => (
                            <div {...defns.detailContainer}>
                                <SchoolIcon {...defns.icons} />
                                <AfitdTypography {...defns.detail}>{el}</AfitdTypography>
                            </div>
                        ))}
                    </div>
                    {conversationExists && (
                        <Badge
                            {...defns.badge}
                            onClick={() =>
                                history.push('/', {
                                    pathname: '/',
                                    state: { currentDashboard: DASHBOARD_MY_MESSAGES, threadId: conversationExists?.threadId },
                                })
                            }
                        >
                            <MessageIcon {...defns.messageIcon} />
                        </Badge>
                    )}
                </div>
                {comingSoon ? (
                    <div {...defns.comingSoonContainer}>
                        <AfitdTypography {...defns.comingSoonText}>Coming Soon</AfitdTypography>
                    </div>
                ) : null}
                {filled ? (
                    <div {...defns.filledContainer}>
                        <AfitdTypography {...defns.filledText}>Already Filled</AfitdTypography>
                    </div>
                ) : null}
                <div {...defns.descriptionTextContainer}>
                    {_map(_split(jobDescription, '\n'), el => (
                        <AfitdTypography {...defns.descriptionText} key={getUniqueKey()}>
                            {el}
                        </AfitdTypography>
                    ))}
                </div>
                <AfitdNote {...defns.note} />
            </div>
            {/* {savedJobSeekers?.length > 0 && (
                <div {...defns.savedList}>
                    <AfitdTypography {...defns.listText}>
                        <strong>{savedJobSeekers?.length} </strong>
                        job seeker{savedJobSeekers?.length > 1 && 's'}
                        <strong> {savedJobSeekers?.length > 1 ? 'have' : 'has'} been saved </strong>
                        to this job
                    </AfitdTypography>
                    <AfitdTypography {...defns.savedListText}>Show list</AfitdTypography>
                </div>
            )} */}

            {/* {appliedJobSeekers?.length > 0 && (
                <div {...defns.appliedList}>
                    <AfitdTypography {...defns.listText}>
                        <strong>{appliedJobSeekers?.length} </strong>
                        job seeker{appliedJobSeekers?.length > 1 && 's'}
                        <strong> applied </strong>
                        to this job
                    </AfitdTypography>
                    <Badge {...defns.applicantsBadge}>
                        <AfitdTypography {...defns.appliedListText}>Show list</AfitdTypography>
                    </Badge>
                </div>
            )} */}

            {/* {listModalOpen && <JobSeekersDialog {...defns.listModal} />} */}
            {/* {editModalShow && <AddJobDialog {...defns.editJobModal} />} */}
            {/* {hiredModalOpen && <HiredDialog {...defns.hiredModal} />} */}
            <AfitdConfirmDialog {...defns.confirmDeleteDialog} />
            {/* <ConfirmationDialog {...defns.limitDialog} /> */}
        </div>
    );
};

export default JobCard;
